/* eslint-disable react/react-in-jsx-scope */
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Save, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useParams, useLocation } from "react-router-dom";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

type Project = {
  id: number;
  name: string;
  access: boolean;
};

type Program = {
  id: number;
  name: string;
  access: boolean;
  projects: Project[];
};

const PermissionSubContractor = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "soustraitant",
  });
  const { id } = useParams();
  const location = useLocation();
  const subcontractor = location.state?.subcontractor ?? "";
  const navigate = useNavigate();
  const { postData } = usePostData();
  const [isLoading, setIsloading] = useState(true);
  const hidePostActions = useHidePostActions();
  const [isLoadingSaving, setIsLoadingSaving] = useState(false);
  const [data, setData] = useState<Program[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);

  const permissionsData = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/project/user-access/${id}`,
    "permissions",
    1000 * 60 * 60,
    true
  );

  useEffect(() => {
    if (permissionsData?.data !== undefined) {
      const dataTemp: Program[] = permissionsData?.data?.length
        ? permissionsData.data.map((program: any) => ({
            id: program.id,
            name: program.name,
            access: program.access,
            projects: program.projects.map((project: any) => ({
              id: project.id,
              name: project.name,
              access: project.access,
            })),
          }))
        : ([] as Program[]);
      setData(dataTemp);

      // Initialise selectedPrograms et selectedProjects
      const selectedProgramsTemp: number[] = [];
      const selectedProjectsTemp: number[] = [];

      dataTemp.forEach((program) => {
        if (program.access) {
          selectedProgramsTemp.push(program.id);
        }
        program.projects.forEach((project) => {
          if (project.access) {
            selectedProjectsTemp.push(project.id);
          }
        });
      });

      setSelectedPrograms(selectedProgramsTemp);
      setSelectedProjects(selectedProjectsTemp);
      setIsloading(false);
    }
  }, [permissionsData?.data]);

  const getProgramCheckboxState = (program: Program) => {
    if (selectedPrograms.includes(program.id)) {
      return true;
    }

    const hasSelectedProjects = program.projects.some((project) =>
      selectedProjects.includes(project.id)
    );

    return hasSelectedProjects ? "indeterminate" : false;
  };

  const toggleProgram = (programId: number) => {
    const isChecked = selectedPrograms.includes(programId);
    const program = data.find((p) => p.id === programId);
    if (!program) return;

    if (isChecked) {
      // On décoche le programme → décocher tous les projets associés
      setSelectedPrograms((prev) => prev.filter((id) => id !== programId));
      setSelectedProjects((prev) =>
        prev.filter((id) => !program.projects.some((proj) => proj.id === id))
      );
    } else {
      // On coche le programme → cocher tous les projets associés
      setSelectedPrograms((prev) => [...prev, programId]);
      setSelectedProjects((prev) => [
        ...prev,
        ...program.projects
          .map((proj) => proj.id)
          .filter((id) => !prev.includes(id)),
      ]);
    }
  };

  const toggleProject = (programId: number, projectId: number) => {
    const isChecked = selectedProjects.includes(projectId);
    const updatedProjects = isChecked
      ? selectedProjects.filter((id) => id !== projectId)
      : [...selectedProjects, projectId];

    setSelectedProjects(updatedProjects);

    const program = data.find((p) => p.id === programId);
    if (!program) return;

    const programProjectIds = program.projects.map((proj) => proj.id);
    const allProjectsChecked = programProjectIds.every((id) =>
      updatedProjects.includes(id)
    );

    if (allProjectsChecked) {
      // Tous les projets sont cochés → cocher le programme
      if (!selectedPrograms.includes(programId)) {
        setSelectedPrograms((prev) => [...prev, programId]);
      }
    } else {
      // Un projet a été décoché → décocher le programme
      setSelectedPrograms((prev) => prev.filter((id) => id !== programId));
    }
  };

  const getSelectedAccessData = () => {
    return data
      .map((program) => {
        const allProjectIds = program.projects.map((proj) => proj.id);
        const selectedInThisProgram = allProjectIds.filter((id) =>
          selectedProjects.includes(id)
        );

        if (selectedInThisProgram.length === 0) {
          // Aucun projet sélectionné ,ne pas inclure ce programme
          return null;
        }

        const isAllSelected =
          selectedInThisProgram.length === allProjectIds.length;

        return {
          programId: program.id,
          projectIds: isAllSelected ? [] : selectedInThisProgram,
        };
      })
      .filter(Boolean); // Supprime les nulls
  };
  const handleSubmit = () => {
    console.log("Programmes cochés :", selectedPrograms);
    console.log("Projets cochés :", selectedProjects);
    console.log("Data à envoyer :", getSelectedAccessData());
    setIsLoadingSaving(true);
    const dataToPost = getSelectedAccessData();
    postData({
      url:
        import.meta.env.VITE_API_ENDPOINT + `/project/user-access-edit/${id}`,
      data: {
        dataToPost,
      },
    }).then((res) => {
      if (res.success !== undefined && res.success) {
        toast({
          title: t("Permission modifiée avec succès"),
        });
        setIsLoadingSaving(false);
        // window.location.href = `dashboard/subcontractors`;
        navigate("/dashboard/subcontractors");
        // window.location.reload();
      }
    });
  };

  return (
    <div>
      <ReactBreadcrumb
        items={[
          {
            href: ``,
            name: t("Mes intervenants"),
          },
        ]}
        isTruncated={false}
      />

      <div className="flex flex-wrap items-center justify-between mt-5">
        <h1
          className="mb-2 mt-4 text-[24px] font-medium inline-block"
          id="program-title"
        >
          {t("Gérer les permissions")}
          {subcontractor && subcontractor !== "" ? " - " : ""}
          <span className="text-orange-500 underline">{subcontractor}</span>
        </h1>

        {/* BOUTON RETOUR */}
        <button
          onClick={() => navigate("/dashboard/subcontractors")}
          className="flex items-center gap-2 text-white bg-secondary px-4 py-2 rounded-lg hover:opacity-90 ml-auto transition"
        >
          <ArrowLeft size={18} />
          Revenir à la liste
        </button>
      </div>

      <div className="mt-4 flex w-full flex-wrap">
        <label className=" mb-3 w-full font-medium text-[#7a7a7a]" htmlFor="">
          {t(
            "Cette interface vous permet de gérer les accès aux programmes et à leurs projets."
          )}{" "}
          <br />
          {t("En sélectionnant l’option")}
        </label>
      </div>

      <div className="mt-5 w-full rounded-[20px] bg-[#F7F9FB] p-5">
        {isLoading ? (
          <>
            <Skeleton height={32} width={200} style={{ marginBottom: 16 }} />

            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="mb-4">
                {/* En-tête du programme */}
                <div className="flex justify-between items-center mb-2">
                  <Skeleton height={24} width={`60%`} />
                  <Skeleton height={16} width={100} />
                </div>
                {/* Contenu des projets */}
                <div className="space-y-2">
                  {Array.from({ length: 2 }).map((__, j) => (
                    <div
                      key={j}
                      className="flex items-center px-4 py-2 rounded bg-[#FFF]"
                    >
                      <Skeleton
                        circle
                        height={16}
                        width={16}
                        style={{ marginRight: 8 }}
                      />
                      <Skeleton height={16} width={`80%`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Bouton */}
            <div className="mt-6">
              <Skeleton height={40} width={120} />
            </div>
          </>
        ) : (
          <>
            <Accordion type="multiple" className="w-full">
              {data.map((program) => (
                <AccordionItem
                  key={program.id}
                  value={`program-${program.id}`}
                  className="items-start w-full px-[18px] rounded-[9px] bg-[#FFFF] py-[10px] hover:no-underline group mt-2"
                >
                  <AccordionTrigger>
                    <div className="flex items-start w-full">
                      <span className="font-medium text-base text-gray-800 w-3/5">
                        {program.name}
                      </span>
                      <label className="flex items-center gap-2 w-2/5 justify-end text-sm text-gray-600">
                        <Checkbox
                          checked={getProgramCheckboxState(program)}
                          onCheckedChange={() => toggleProgram(program.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {t("Accéder à tous les projets de ce programme")}
                      </label>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent>
                    <div className="mt-3 flex flex-col px-4">
                      {program.projects.map((project) => (
                        <div
                          key={project.id}
                          className="flex items-center px-4 py-2 border-b border-gray-200 bg-[#F9FAFB] hover:bg-[#EDF2F7] "
                        >
                          <Checkbox
                            checked={selectedProjects.includes(project.id)}
                            onCheckedChange={() =>
                              toggleProject(program.id, project.id)
                            }
                            className="mr-3"
                          />
                          <span className="text-sm text-gray-700">
                            {project.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {!hidePostActions && (
            <button
              onClick={handleSubmit}
              className={`mt-6 px-6 py-3 text-white rounded-lg hover:opacity-90 transition flex items-center gap-2
              ${
                isLoadingSaving
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-secondary"
              }`}
              disabled={isLoadingSaving}
            >
              <Save size={18} />
              {isLoadingSaving
                ? t("... sauvegarde en cours")
                : t("Enregistrer les paramètres")}
            </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PermissionSubContractor;
